import React, { useEffect, useMemo, useState } from "react";
import { getTransform } from "../../../../../utils/getTransform";
import { TOOL_PREP_PREVIEW_BG } from "../../../../../utils/editorEntrySession";
import {
  aspectFromSize,
  computeFittedStageSize,
  TOOL_PREP_STAGE_INSET,
  useImageNaturalSize,
  useReportSubjectPlacement,
} from "../../../lib/toolPrepImageLayout";
import { useToolPrepSubjectObject } from "../../../hooks/useToolPrepSubjectObject";
import RoundLoader from "../../../../../components/ui/RoundLoader";
import ToolPrepSubjectBoundingBox from "../../../shared/Popup/ToolPrepSubjectBoundingBox";
import {
  ToolPrepSubjectCropGhost,
  subjectMainImageStyleForCrop,
  subjectWrapperStyleForCrop,
} from "../../../shared/Popup/ToolPrepSubjectCropGhost";
import ToolPrepCanvasStage from "../../../shared/Popup/ToolPrepCanvasStage";
import "../../../shared/Popup/ToolPrepStage.css";
import "./BackgroundColorPreview.css";

const BackgroundColorPreview = ({
  imageUrl = null,
  uploadedImageUrl = null,
  loading = false,
  applying = false,
  applyingLabel = "Applying background…",
  previewBg = TOOL_PREP_PREVIEW_BG.TRANSPARENT,
  selectedColor = "#FFFFFF",
  emptyLabel = "Upload an image to preview",
  showSubjectBox = false,
  onSubjectPlacement = null,
}) => {
  const naturalSize = useImageNaturalSize(imageUrl || uploadedImageUrl);
  const [stageSize, setStageSize] = useState({ width: 420, height: 420 });
  const stageRef = React.useRef(null);
  const isTransparent = previewBg === TOOL_PREP_PREVIEW_BG.TRANSPARENT;
  const isOriginal = previewBg === TOOL_PREP_PREVIEW_BG.ORIGINAL;
  const isColor =
    previewBg === TOOL_PREP_PREVIEW_BG.COLOR ||
    previewBg === TOOL_PREP_PREVIEW_BG.WHITE;
  const isEmpty = !imageUrl && !uploadedImageUrl && !loading;
  const useChecker = isTransparent || isEmpty;

  const imageAspect = useMemo(
    () => aspectFromSize(naturalSize.width, naturalSize.height, 1),
    [naturalSize]
  );

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => {
      const fitted = computeFittedStageSize(
        el.clientWidth,
        el.clientHeight,
        imageAspect,
        { inset: TOOL_PREP_STAGE_INSET }
      );
      if (fitted) setStageSize(fitted);
    };
    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [imageAspect, imageUrl]);

  const transform = useMemo(() => {
    if (!naturalSize.width || !naturalSize.height || !stageSize.width) {
      return { width: 0, height: 0, x: 0, y: 0 };
    }
    return getTransform(
      naturalSize.width,
      naturalSize.height,
      stageSize.width,
      stageSize.height,
      "fit"
    );
  }, [naturalSize, stageSize]);

  const fitFrame = useMemo(() => {
    if (!(transform.width > 0)) return null;
    return {
      x: transform.x,
      y: transform.y,
      width: transform.width,
      height: transform.height,
    };
  }, [transform.x, transform.y, transform.width, transform.height]);

  const interactive = showSubjectBox && Boolean(imageUrl) && !loading && !applying;
  const [isCropping, setIsCropping] = useState(false);
  const { object, setObject, wrapperStyle, imageStyle, contentCrop, crop, cropExtent, originalImageSize } = useToolPrepSubjectObject({
    enabled: interactive,
    imageUrl,
    fitFrame,
    resetKey: imageUrl,
  });

  useReportSubjectPlacement(
    onSubjectPlacement,
    object,
    contentCrop,
    stageSize,
    imageUrl
  );

  const fitStyle = fitFrame
    ? {
        left: fitFrame.x,
        top: fitFrame.y,
        width: fitFrame.width,
        height: fitFrame.height,
      }
    : undefined;

  return (
    <ToolPrepCanvasStage
      className="bctp-preview"
      ref={stageRef}
      metaImageUrl={imageUrl || uploadedImageUrl}
      metaWidth={naturalSize.width}
      metaHeight={naturalSize.height}
    >
      <div
        className={`bctp-preview__stage${
          useChecker ? " bctp-preview__stage--checker" : ""
        }${isOriginal && !isEmpty ? " bctp-preview__stage--original" : ""}`}
        style={{
          width: stageSize.width,
          height: stageSize.height,
          backgroundColor: isColor && !isEmpty ? selectedColor : undefined,
        }}
      >
        <div className="tool-prep-stage__clip">
          {isOriginal && uploadedImageUrl ? (
            <img
              src={uploadedImageUrl}
              alt=""
              className={
                fitFrame
                  ? "bctp-preview__uploaded-bg"
                  : "bctp-preview__uploaded-bg bctp-preview__uploaded-bg--fallback"
              }
              style={fitStyle}
              draggable={false}
            />
          ) : null}
          {loading ? (
            <div className="bctp-preview-loading">
              <RoundLoader size="md" label="Removing background…" />
            </div>
          ) : applying ? (
            <div className="bctp-preview-loading">
              <RoundLoader size="md" label={applyingLabel} />
            </div>
          ) : imageUrl && interactive && object && wrapperStyle ? (
            <div
              className="bctp-preview__subject"
              style={subjectWrapperStyleForCrop(wrapperStyle, isCropping)}
            >
              <ToolPrepSubjectCropGhost
                active={isCropping}
                src={imageUrl}
                style={imageStyle}
              />
              <img
                src={imageUrl}
                alt="Background color preview"
                className="bctp-preview__image"
                style={subjectMainImageStyleForCrop(imageStyle, {
                  isCropping,
                  crop,
                  originalImageSize,
                  object,
                })}
                draggable={false}
              />
            </div>
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt="Background color preview"
              className={
                fitFrame
                  ? "bctp-preview__image"
                  : "bctp-preview__image bctp-preview__image--fallback"
              }
              style={fitStyle}
              draggable={false}
            />
          ) : (
            <div className="bctp-preview__empty">{emptyLabel}</div>
          )}
        </div>
        {imageUrl && interactive && object && wrapperStyle ? (
          <div className="tool-prep-stage__chrome">
            <ToolPrepSubjectBoundingBox
              enabled
              interactive
              frame={object}
              crop={crop}
              cropExtent={cropExtent}
              originalImageSize={originalImageSize}
              onChange={setObject}
              onCroppingChange={setIsCropping}
              stageSize={stageSize}
            />
          </div>
        ) : null}
      </div>
    </ToolPrepCanvasStage>
  );
};

export default BackgroundColorPreview;
